import React, { useState, useEffect, useRef } from 'react';
import styles from './AddSlideForm.module.scss';
import axios from 'axios';
import ImageSizeGuide from '../../../components/Admin/ImageSizeGuide/ImageSizeGuide';

const AddSlideForm = ({ 
  selectedCaruselId, 
  products, 
  blogs, 
  banners, 
  categories, 
  fetchCaruselItems, 
  config 
}) => {
  const [newSlide, setNewSlide] = useState({ itemId: '', itemType: 'blog', order: 1 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [carouselFiles, setCarouselFiles] = useState({ desktop: null, mobile: null });
  const dropdownRef = useRef(null);

  useEffect(() => {
    setNewSlide({ itemId: '', itemType: 'blog', order: 1 });
    setErrors({});
    setSearchTerm('');
    setCarouselFiles({ desktop: null, mobile: null });
  }, [selectedCaruselId]);

  // Обробка кліків поза дропдауном
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!selectedCaruselId) {
      newErrors.carousel = 'Спочатку оберіть або створіть карусель';
    }
    
    if (!newSlide.itemId) {
      newErrors.itemId = 'Оберіть елемент для слайду';
    }
    
    if (!newSlide.order || newSlide.order < 1) {
      newErrors.order = 'Порядок має бути додатним числом';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddSlide = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('caruselId', selectedCaruselId);
      formData.append('itemId', newSlide.itemId);
      formData.append('itemType', newSlide.itemType);
      formData.append('order', newSlide.order);
      
      // Додаємо файли каруселі, якщо вони є
      if (carouselFiles.desktop) {
        formData.append('carouselImageDesktop', carouselFiles.desktop);
      }
      if (carouselFiles.mobile) {
        formData.append('carouselImageMobile', carouselFiles.mobile);
      }

      await axios.post(
        '/api/admin/carusels/add-slide',
        formData,
        {
          ...config,
          headers: {
            ...config.headers,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      // Скидання форми після успішного додавання
      setNewSlide({ itemId: '', itemType: newSlide.itemType, order: 1 });
      setCarouselFiles({ desktop: null, mobile: null });
      setErrors({});
      
      // Очищаємо файлові інпути
      const desktopInput = document.getElementById('carousel-desktop');
      const mobileInput = document.getElementById('carousel-mobile');
      if (desktopInput) desktopInput.value = '';
      if (mobileInput) mobileInput.value = '';
      
      fetchCaruselItems();
      
    } catch (error) {
      console.error('Error adding slide:', error);
      
      let errorMessage = 'Помилка при додаванні слайду';
      
      if (error.response?.status === 413) {
        errorMessage = 'Файл занадто великий! Стисніть картинки до 1MB перед завантаженням.';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Час очікування вичерпано. Спробуйте менший файл або повторіть пізніше.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAllItems = () => {
    switch (newSlide.itemType) {
      case 'product':
        return products.map((product) => ({
          id: product._id,
          name: product.name,
          image: product.imageFirst || product.imageUrl
        }));
      case 'blog':
        return blogs.map((blog) => ({
          id: blog._id,
          name: blog.title,
          image: blog.imageUrl
        }));
      case 'banner':
        return banners.map((banner) => ({
          id: banner._id,
          name: banner.title,
          image: banner.imageUrl,
          description: banner.description
        }));
      default:
        return [];
    }
  };

  const getItemOptions = () => {
    const allItems = getAllItems();
    
    // Фільтруємо по пошуковому терміну
    if (searchTerm.trim()) {
      return allItems.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase().trim())
      );
    } else {
      // Коли немає пошуку, показуємо тільки перші 50 елементів
      return allItems.slice(0, 50);
    }
  };

  const getSelectedItemName = () => {
    const items = getItemOptions();
    const selectedItem = items.find(item => item.id === newSlide.itemId);
    return selectedItem?.name || `Оберіть ${getTypeLabel()}`;
  };

  const getTypeLabel = () => {
    switch (newSlide.itemType) {
      case 'product': return 'продукт';
      case 'blog': return 'блог';
      case 'banner': return 'банер';
      default: return 'елемент';
    }
  };

  const handleItemSelect = (itemId) => {
    setNewSlide({ ...newSlide, itemId });
    setShowDropdown(false);
    setSearchTerm('');
    setErrors({ ...errors, itemId: '' });
  };

  // Функція автоматичного стиснення картинки
  const compressImage = (file, maxSizeKB = 800) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Розраховуємо нові розміри (зберігаючи пропорції)
        const maxWidth = 1920;
        const maxHeight = 1080;
        let { width, height } = img;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        canvas.width = width;
        canvas.height = height;

        // Малюємо зображення на canvas
        ctx.drawImage(img, 0, 0, width, height);

        // Пробуємо різну якість до досягнення потрібного розміру
        let quality = 0.9;
        const tryCompress = () => {
          canvas.toBlob((blob) => {
            if (blob.size <= maxSizeKB * 1024 || quality <= 0.1) {
              // Створюємо новий File об'єкт
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              resolve(compressedFile);
            } else {
              quality -= 0.1;
              tryCompress();
            }
          }, 'image/jpeg', quality);
        };
        
        tryCompress();
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const handleCarouselFileChange = async (type, file) => {
    if (!file) return;

    // Показуємо стан завантаження
    setErrors(prev => ({
      ...prev,
      [type]: 'Обробка файлу...'
    }));

    try {
      let processedFile = file;
      
      // Якщо файл більше 1MB - автоматично стискаємо
      if (file.size > 1024 * 1024) {
        processedFile = await compressImage(file, 800); // до 800KB
        
        const originalSizeMB = (file.size / 1024 / 1024).toFixed(1);
        const compressedSizeKB = (processedFile.size / 1024).toFixed(0);
        
        setErrors(prev => ({
          ...prev,
          [type]: `✅ Файл автоматично стиснуто з ${originalSizeMB}MB до ${compressedSizeKB}KB`,
          [`${type}_success`]: true // Позначка для зеленого стилю
        }));
        
        // Очищаємо повідомлення через 3 секунди
        setTimeout(() => {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[type];
            delete newErrors[`${type}_success`];
            return newErrors;
          });
        }, 3000);
      } else {
        // Файл нормального розміру
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[type];
          return newErrors;
        });
      }

      setCarouselFiles(prev => ({
        ...prev,
        [type]: processedFile
      }));
      
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        [type]: 'Помилка обробки файлу. Спробуйте інший файл.'
      }));
    }
  };

  const removeCarouselFile = (type) => {
    setCarouselFiles(prev => ({
      ...prev,
      [type]: null
    }));
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    if (!showDropdown) {
      setShowDropdown(true);
    }
  };

  const handleTypeChange = (newType) => {
    setNewSlide({ ...newSlide, itemType: newType, itemId: '' });
    setSearchTerm('');
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowDropdown(false);
      setSearchTerm('');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const options = getItemOptions();
      if (options.length === 1) {
        handleItemSelect(options[0].id);
      }
    }
  };

  // Функція для виділення знайденого тексту
  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm.trim()) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} className={styles.highlight}>{part}</span>
      ) : part
    );
  };

  return (
    <div className={styles.addSlideForm}>
      <div className={styles.formHeader}>
        <h2 className={styles.formTitle}>
          <span className={styles.icon}>➕</span>
          Додати новий слайд
        </h2>
        <button 
          type="button"
          className={styles.sizeGuideButton}
          onClick={() => setShowSizeGuide(true)}
          title="Довідник розмірів картинок"
        >
          � Розміри картинок
        </button>
        {errors.carousel && (
          <div className={styles.alertError}>{errors.carousel}</div>
        )}
      </div>

      <form onSubmit={handleAddSlide} className={styles.form}>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Тип контенту</label>
            <div className={styles.radioGroup}>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  value="blog"
                  checked={newSlide.itemType === 'blog'}
                  onChange={(e) => handleTypeChange(e.target.value)}
                />
                <span className={styles.radioCustom}></span>
                📝 Блог
              </label>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  value="product"
                  checked={newSlide.itemType === 'product'}
                  onChange={(e) => handleTypeChange(e.target.value)}
                />
                <span className={styles.radioCustom}></span>
                🛍️ Продукт
              </label>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  value="banner"
                  checked={newSlide.itemType === 'banner'}
                  onChange={(e) => handleTypeChange(e.target.value)}
                />
                <span className={styles.radioCustom}></span>
                🎯 Банер
              </label>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Порядок</label>
            <input
              type="number"
              min="1"
              value={newSlide.order}
              onChange={(e) => setNewSlide({ ...newSlide, order: parseInt(e.target.value) || 1 })}
              className={`${styles.input} ${errors.order ? styles.inputError : ''}`}
              placeholder="1"
            />
            {errors.order && <span className={styles.errorText}>{errors.order}</span>}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Оберіть {getTypeLabel()}</label>
          <div className={`${styles.customSelect} ${errors.itemId ? styles.selectError : ''}`} ref={dropdownRef}>
            <button 
              type="button" 
              className={styles.selectButton}
              onClick={() => setShowDropdown(!showDropdown)}
            >
              <span className={styles.selectText}>{getSelectedItemName()}</span>
              <span className={`${styles.selectArrow} ${showDropdown ? styles.rotated : ''}`}>⌄</span>
            </button>
            
            {showDropdown && (
              <div className={styles.selectDropdown}>
                <div className={styles.searchContainer}>
                  <div className={styles.searchInputWrapper}>
                    <input
                      type="text"
                      placeholder={`Пошук ${getTypeLabel()}...`}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onKeyDown={handleSearchKeyDown}
                      className={styles.searchInput}
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                    {searchTerm && (
                      <button
                        type="button"
                        className={styles.clearSearch}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSearchTerm('');
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  {searchTerm ? (
                    getItemOptions().length > 0 && (
                      <div className={styles.searchResults}>
                        Знайдено: {getItemOptions().length}
                      </div>
                    )
                  ) : (
                    <div className={styles.searchHint}>
                      Показані перші 50 елементів. Використовуйте пошук для знаходження конкретного {getTypeLabel()}.
                    </div>
                  )}
                </div>
                
                <div className={styles.optionsList}>
                  {getItemOptions().length === 0 ? (
                    <div className={styles.selectOption}>
                      {searchTerm.trim() 
                        ? `Не знайдено жодного ${getTypeLabel()} за запитом "${searchTerm}" (всього доступно: ${getAllItems().length})`
                        : 'Немає доступних елементів цього типу'
                      }
                    </div>
                  ) : (
                    getItemOptions().map((item) => (
                      <div
                        key={item.id}
                        className={`${styles.selectOption} ${item.id === newSlide.itemId ? styles.selected : ''}`}
                        onClick={() => handleItemSelect(item.id)}
                      >
                        {item.image && (
                          <img 
                            src={item.image} 
                            alt={item.name}
                            className={styles.optionImage}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        <div className={styles.optionContent}>
                          <div className={styles.optionName}>
                            {highlightSearchTerm(item.name, searchTerm)}
                          </div>
                          {item.description && (
                            <div className={styles.optionDescription}>
                              {highlightSearchTerm(item.description, searchTerm)}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          {errors.itemId && <span className={styles.errorText}>{errors.itemId}</span>}
        </div>

        {/* Додаткові картинки для каруселі */}
        <div className={styles.fileUploadSection}>
          <h3 className={styles.sectionTitle}>
            🖼️ Окремі картинки для каруселі (опціонально)
          </h3>
          <p className={styles.sectionDescription}>
            Завантажте окремі картинки для каруселі. 
            <strong>🤖 Файли більше 1MB автоматично стискаються</strong> до оптимального розміру.
            Підтримуються JPG, PNG, WebP.
          </p>

          <div className={styles.fileUploadGroup}>
            {/* Desktop Image */}
            <div className={styles.fileUploadItem}>
              <label className={styles.fileLabel}>
                🖥️ Картинка для десктопу
                <span className={styles.fileSizeHint}>(рекомендовано: 1200x600px, будь-який розмір)</span>
              </label>
              
              {carouselFiles.desktop ? (
                <div className={styles.filePreview}>
                  <img 
                    src={URL.createObjectURL(carouselFiles.desktop)} 
                    alt="Desktop preview"
                    className={styles.previewImage}
                  />
                  <div className={styles.fileInfo}>
                    <span className={styles.fileName}>{carouselFiles.desktop.name}</span>
                    <button 
                      type="button" 
                      className={styles.removeFileButton}
                      onClick={() => removeCarouselFile('desktop')}
                    >
                      🗑️ Видалити
                    </button>
                  </div>
                </div>
              ) : (
                <div className={styles.fileInputWrapper}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleCarouselFileChange('desktop', e.target.files[0])}
                    className={styles.fileInput}
                    id="carousel-desktop"
                  />
                  <label htmlFor="carousel-desktop" className={styles.fileInputButton}>
                    📁 Оберіть файл для десктопу
                  </label>
                </div>
              )}
              {errors.desktop && (
                <span className={`${styles.errorText} ${errors.desktop_success ? styles.successText : ''}`}>
                  {errors.desktop}
                </span>
              )}
            </div>

            {/* Mobile Image */}
            <div className={styles.fileUploadItem}>
              <label className={styles.fileLabel}>
                📱 Картинка для мобільного
                <span className={styles.fileSizeHint}>(рекомендовано: 600x800px, будь-який розмір)</span>
              </label>
              
              {carouselFiles.mobile ? (
                <div className={styles.filePreview}>
                  <img 
                    src={URL.createObjectURL(carouselFiles.mobile)} 
                    alt="Mobile preview"
                    className={styles.previewImage}
                  />
                  <div className={styles.fileInfo}>
                    <span className={styles.fileName}>{carouselFiles.mobile.name}</span>
                    <button 
                      type="button" 
                      className={styles.removeFileButton}
                      onClick={() => removeCarouselFile('mobile')}
                    >
                      🗑️ Видалити
                    </button>
                  </div>
                </div>
              ) : (
                <div className={styles.fileInputWrapper}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleCarouselFileChange('mobile', e.target.files[0])}
                    className={styles.fileInput}
                    id="carousel-mobile"
                  />
                  <label htmlFor="carousel-mobile" className={styles.fileInputButton}>
                    📁 Оберіть файл для мобільного
                  </label>
                </div>
              )}
              {errors.mobile && (
                <span className={`${styles.errorText} ${errors.mobile_success ? styles.successText : ''}`}>
                  {errors.mobile}
                </span>
              )}
            </div>
          </div>
        </div>

        {errors.submit && (
          <div className={styles.alertError}>{errors.submit}</div>
        )}

        <div className={styles.formActions}>
          <button 
            type="submit" 
            className={styles.submitButton}
            disabled={isSubmitting || !selectedCaruselId}
          >
            {isSubmitting ? (
              <>
                <span className={styles.spinner}></span>
                Додавання...
              </>
            ) : (
              <>
                <span className={styles.buttonIcon}>➕</span>
                Додати слайд
              </>
            )}
          </button>
        </div>
      </form>
      
      {showSizeGuide && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <button 
              className={styles.closeButton}
              onClick={() => setShowSizeGuide(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}
      
      <ImageSizeGuide
        isOpen={showSizeGuide}
        onClose={() => setShowSizeGuide(false)}
      />
    </div>
  );
};

export default AddSlideForm;
